import React, { useState, useEffect, useRef } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Collapse,
  LinearProgress,
  Alert,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ViewKanban as ViewKanbanIcon,
  TableRows as TableRowsIcon,
  OpenInNew as OpenInNewIcon,
  FilterList as FilterListIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProjects, setProjectPhase } from '../services/api';
import { differenceInDays } from 'date-fns';
import PageSearchBar from '../components/PageSearchBar';
import WipByDisciplineStrip from '../components/WipByDisciplineStrip';

const PHASE_NAMES = [
  'Proposal',
  'Engineering & Kick-off',
  'Rough-in',
  'Pre-Final',
  'Final',
  'Final Billing',
  'AfterCare',
];

const HEALTH_COLORS = {
  green: 'success',
  yellow: 'warning',
  red: 'error',
};

function fmtCurrency(val) {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return '—';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// ─── OptiPortal Quick-Link ────────────────────────────────────────────────────

const OPTI_JSRDTL_URL = (jobNum) =>
  `https://optiportal.audiocommand.com/Report-Viewer?key=JSR_DTwLoc&_CRJob=${encodeURIComponent(jobNum)}&renderas=pdf`;

/** Small chip that opens the JSRDTL report for a job number. Stops propagation so the row/card click still works. */
// Vin Baus 2026-07-01: the green "OptiPortal" chip actually opens the JSRDTL
// (D-Tools Job Status Report with Location). Relabeling to "JSRDTL" so PMs
// know exactly which OptiPortal report will open.
const OptiChip = ({ jobNumber }) => {
  if (!jobNumber) return null;
  return (
    <Chip
      component="a"
      href={OPTI_JSRDTL_URL(jobNumber)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      icon={<OpenInNewIcon sx={{ fontSize: '0.75rem !important' }} />}
      label="JSRDTL"
      title="Open D-Tools Job Status Report with Location (JSRDTL) in OptiPortal"
      size="small"
      sx={{
        bgcolor: '#2e7d32',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '0.65rem',
        cursor: 'pointer',
        '& .MuiChip-icon': { color: 'white' },
        '&:hover': { bgcolor: '#1b5e20' },
        textDecoration: 'none',
      }}
    />
  );
};

// ─── Kanban Card ─────────────────────────────────────────────────────────────

const ProjectCard = ({ project, onClick, isManagement, canEditProject, onPhaseChange }) => {
  const daysInPhase = project.started_at
    ? differenceInDays(new Date(), new Date(project.started_at))
    : 0;

  return (
    <Card
      elevation={2}
      sx={{
        mb: 2,
        '&:hover': {
          elevation: 4,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s',
        },
      }}
    >
      <CardActionArea onClick={onClick}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1, pr: 1, lineHeight: 1.2 }}>
              {project.name}
            </Typography>
            <Chip
              label={project.health_status}
              size="small"
              color={HEALTH_COLORS[project.health_status]}
              sx={{ textTransform: 'capitalize', flexShrink: 0 }}
            />
          </Box>

          {project.primary_job_number && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" color="primary.main" fontWeight="bold">
                Job #{project.primary_job_number}
              </Typography>
              <OptiChip jobNumber={project.primary_job_number} />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {project.client_name}
          </Typography>

          {isManagement && project.total_proposal_value > 0 && (
            <Typography variant="body2" color="success.main" fontWeight="medium">
              {fmtCurrency(project.total_proposal_value)}
            </Typography>
          )}

          <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              PM: {project.pm_name || 'Unassigned'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {daysInPhase}d
            </Typography>
          </Box>

          {project.overdue_tasks > 0 && (
            <Chip
              label={`${project.overdue_tasks} overdue`}
              size="small"
              color="error"
              sx={{ mt: 1 }}
            />
          )}
          {canEditProject && (
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
              {project.current_phase > 1 && (
                <Tooltip title={`Move to ${PHASE_NAMES[project.current_phase - 2]}`}>
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPhaseChange(project.id, project.current_phase - 1, project.name);
                    }}
                    sx={{ border: '1px solid', borderColor: 'warning.main', p: 0.3 }}
                  >
                    <NavigateBeforeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {project.current_phase < 7 && (
                <Tooltip title={`Move to ${PHASE_NAMES[project.current_phase]}`}>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPhaseChange(project.id, project.current_phase + 1, project.name);
                    }}
                    sx={{ border: '1px solid', borderColor: 'success.main', p: 0.3 }}
                  >
                    <NavigateNextIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// ─── Kanban Column ────────────────────────────────────────────────────────────

const PhaseColumn = ({ phase, projects, onProjectClick, isManagement, canEditProject, onPhaseChange }) => (
  <Paper elevation={1} sx={{ p: 2, minHeight: 400, bgcolor: 'grey.50' }}>
    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h6" fontWeight="bold">
        {PHASE_NAMES[phase - 1]}
      </Typography>
      <Chip label={projects.length} size="small" color="primary" />
    </Box>
    <Box>
      {projects.map((project) => (
        <ProjectCard
          key={project.id}
          project={project}
          onClick={() => onProjectClick(project.id)}
          isManagement={isManagement}
          canEditProject={canEditProject}
          onPhaseChange={onPhaseChange}
        />
      ))}
      {projects.length === 0 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No projects
          </Typography>
        </Box>
      )}
    </Box>
  </Paper>
);

// ─── Table View ───────────────────────────────────────────────────────────────

const DEFAULT_COL_WIDTHS = {
  job: 140, name: 250, client: 170, discipline: 105, phase: 155, pm: 140, sales: 140, bill: 100, warranty: 100, value: 125, ar: 105, health: 90,
};

const STORAGE_KEY = 'acs_projects_col_widths_v2';

function loadColWidths() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return { ...DEFAULT_COL_WIDTHS, ...JSON.parse(saved) };
  } catch {}
  return { ...DEFAULT_COL_WIDTHS };
}

const OPTI_REPORTS = [
  { label: 'JSRDTL', key: 'JSR_DTwLoc', color: '#2e7d32' },
  { label: 'Dash',   key: 'PMDASH',     color: '#1565c0' },
  { label: 'Prop',   key: 'PROPOSAL',   color: '#e65100' },
  { label: 'LH',     key: 'LH',         color: '#00695c' },
  { label: 'AR',     key: 'AR',         color: '#c62828' },
  { label: 'CO',     key: 'CO',         color: '#6a1b9a' },
];

const SORT_KEY_MAP = {
  job:    'primary_job_number',
  name:   'name',
  client: 'client_name',
  discipline: '_discipline',
  phase:  'current_phase',
  pm:     'pm_name',
  sales:  'sales_rep_name',
  value:  'total_proposal_value',
  ar:     'ar_number',
  health: 'health_status',
  bill:   'final_bill_status',
  warranty: 'warranty_start_date',
};

// ─── Discipline derivation (Tom Clancy 2026-07-13) ────────────────────────────
//
// The WIP table doesn't store a discipline field — disciplines are encoded
// inside `job_type` / `sage_job_type` as region+discipline codes
// (NYCAV, LIAV, LISHD, NYCLTG, ITNYC, …). We map those to the six labels
// Tom uses when he sorts the WIP: AV, AV/IT, Shades, Lighting, Fixtures, D&E.
//
// Precedence: D&E wins (`is_de_labor` is a boolean tag orthogonal to job_type
// — a D&E line can be inside any discipline; Tom treats them as their own
// bucket). Then Fixtures (lighting job whose name mentions fixtures / LT-F).
// Then the letter-code mapping.
const DISCIPLINES = ['AV', 'AV/IT', 'Shades', 'Lighting', 'Fixtures', 'D&E'];

// Chip colors per discipline. Kept muted so the WIP table doesn't turn
// into a rainbow. Aligned loosely with how each discipline reads in the
// ACS reference material (blue = AV, teal = IT, amber = shades, warm
// yellow = lighting, purple = fixtures, grey = D&E).
const DISCIPLINE_COLORS = {
  'AV':       { bg: '#e3f2fd', fg: '#0d47a1' },
  'AV/IT':    { bg: '#e0f2f1', fg: '#00695c' },
  'Shades':   { bg: '#fff3e0', fg: '#8f4b00' },
  'Lighting': { bg: '#fffbea', fg: '#8a6d00' },
  'Fixtures': { bg: '#f3e5f5', fg: '#6a1b9a' },
  'D&E':      { bg: '#eceff1', fg: '#37474f' },
};

function deriveDiscipline(project) {
  if (!project) return '';
  if (project.is_de_labor) return 'D&E';
  const jt = (project.job_type || project.sage_job_type || '').toUpperCase();
  const name = (project.name || '').toUpperCase();
  // Fixtures: any lighting job flagged as fixtures in the name.
  // ACS convention: 'LT Fixtures', 'Lighting Fixtures', 'LT-F', 'FIXTURES'.
  const isLighting = jt.endsWith('LTG');
  if (isLighting && /(FIXTURE|LT-?F\b|LT\s*FIX)/.test(name)) return 'Fixtures';
  if (jt.startsWith('IT')) return 'AV/IT';
  if (jt.endsWith('SHD')) return 'Shades';
  if (isLighting) return 'Lighting';
  if (jt.endsWith('AV') || jt.endsWith('HT')) return 'AV'; // HT (home theater) rolls into AV
  return '';
}

const ProjectTable = ({ projects, onProjectClick, isManagement, canEditProject, onPhaseChange }) => {
  const navigate = useNavigate();
  const [colWidths, setColWidths] = useState(loadColWidths);
  const resizeRef = React.useRef(null); // { col, startX, startW }

  // ── Sort state ───────────────────────────────────────────────
  const [sortBy,  setSortBy]  = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [sortCycle, setSortCycle] = useState(0); // 0=none,1=asc,2=desc

  const handleSort = (col) => {
    const key = SORT_KEY_MAP[col];
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir('asc');
      setSortCycle(1);
    } else if (sortCycle === 1) {
      setSortDir('desc');
      setSortCycle(2);
    } else {
      // reset
      setSortBy('');
      setSortDir('asc');
      setSortCycle(0);
    }
  };

  // Compute _discipline once per project (used both for the column
  // display and as the sort key). See deriveDiscipline() up top.
  const projectsWithDiscipline = projects.map(p =>
    p._discipline ? p : { ...p, _discipline: deriveDiscipline(p) }
  );

  const sorted = [...projectsWithDiscipline].sort((a, b) => {
    if (!sortBy) return 0;
    let aVal = a[sortBy] ?? '';
    let bVal = b[sortBy] ?? '';
    if (sortBy === 'total_proposal_value') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else if (sortBy === 'current_phase') {
      aVal = parseInt(aVal) || 0;
      bVal = parseInt(bVal) || 0;
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1  : -1;
    return 0;
  });

  // ── Expand state ─────────────────────────────────────────────
  const [expandedRow, setExpandedRow] = useState(null);

  const handleRowClick = (e, projectId) => {
    setExpandedRow((prev) => (prev === projectId ? null : projectId));
  };

  // ── Column resize ────────────────────────────────────────────
  const startResize = (e, col) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { col, startX: e.clientX, startW: colWidths[col] };

    const onMove = (moveEvent) => {
      const dx = moveEvent.clientX - resizeRef.current.startX;
      const newW = Math.max(60, resizeRef.current.startW + dx);
      setColWidths((prev) => ({ ...prev, [resizeRef.current.col]: newW }));
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setColWidths((prev) => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prev)); } catch {}
        return prev;
      });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const ResizeHandle = ({ col }) => (
    <Box
      onMouseDown={(e) => startResize(e, col)}
      sx={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: 6,
        cursor: 'col-resize',
        zIndex: 1,
        '&:hover': { bgcolor: 'primary.main', opacity: 0.4 },
      }}
    />
  );

  const th = (col, label) => {
    const key = SORT_KEY_MAP[col];
    const active = sortBy === key && sortCycle > 0;
    return (
      <TableCell
        key={col}
        sx={{
          fontWeight: 'bold',
          position: 'relative',
          width: colWidths[col],
          minWidth: 60,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          userSelect: 'none',
        }}
      >
        <TableSortLabel
          active={active}
          direction={active ? sortDir : 'asc'}
          onClick={() => handleSort(col)}
          sx={{ '& .MuiTableSortLabel-icon': { opacity: active ? 1 : 0.3 } }}
        >
          {label}
        </TableSortLabel>
        <ResizeHandle col={col} />
      </TableCell>
    );
  };

  // +1 for the Discipline column added 2026-07-13.
  const colCount = isManagement ? 12 : 10;

  return (
    <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 780 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            {th('job',    'Job #')}
            {th('name',   'Project Name')}
            {th('client', 'Client')}
            {th('discipline', 'Discipline')}
            {th('phase',  'Phase')}
            {th('pm',     'PM')}
            {th('sales',  'Sales Rep')}
            {th('bill',   'Final Bill')}
            {th('warranty','Warranty')}
            {isManagement && th('value',  'Total Value')}
            {isManagement && th('ar',     'AR #')}
            {th('health', 'Health')}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((project) => (
            <React.Fragment key={project.id}>
              {/* ── Data row ── */}
              <TableRow
                hover
                selected={expandedRow === project.id}
                sx={{
                  cursor: 'pointer',
                  bgcolor: expandedRow === project.id ? 'action.selected' : undefined,
                }}
                onClick={(e) => handleRowClick(e, project.id)}
              >
                <TableCell sx={{ width: colWidths.job, overflow: 'hidden' }}>
                  {project.primary_job_number ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="primary.main" fontWeight="bold">
                        {project.primary_job_number}
                      </Typography>
                      <OptiChip jobNumber={project.primary_job_number} />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ width: colWidths.name, overflow: 'hidden' }}>
                  <Typography variant="body2" fontWeight="medium" noWrap>
                    {project.name}
                  </Typography>
                  {project.site_address && (
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {project.site_address}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ width: colWidths.client, overflow: 'hidden' }}>
                  <Typography variant="body2" noWrap>{project.client_name}</Typography>
                </TableCell>
                <TableCell sx={{ width: colWidths.discipline, overflow: 'hidden' }}>
                  {project._discipline ? (
                    <Chip
                      label={project._discipline}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.72rem',
                        fontWeight: 500,
                        bgcolor: DISCIPLINE_COLORS[project._discipline]?.bg || 'grey.200',
                        color:   DISCIPLINE_COLORS[project._discipline]?.fg || 'text.primary',
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ width: colWidths.phase, overflow: 'visible' }}>
                  {canEditProject ? (
                    <Select
                      size="small"
                      value={project.current_phase}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newPhase = e.target.value;
                        if (newPhase !== project.current_phase) {
                          onPhaseChange(project.id, newPhase, project.name, true);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        height: 32,
                        minWidth: 130,
                        '.MuiSelect-select': { py: 0.5 },
                      }}
                    >
                      {PHASE_NAMES.map((name, i) => (
                        <MenuItem key={i + 1} value={i + 1} sx={{ fontSize: '0.8rem' }}>
                          {i + 1}. {name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Chip
                      label={PHASE_NAMES[project.current_phase - 1]}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  )}
                  {/* Pre-Final / Final dates cross-ref (Tom Clancy 2026-08-05) */}
                  {(project.prefinal_end || project.final_end) && (
                    <Box sx={{ mt: 0.5, fontSize: 11, lineHeight: 1.25, color: 'text.secondary' }}>
                      {project.prefinal_end && (
                        <div>
                          <strong>Pre-Final End:</strong>{' '}
                          <span style={{
                            color: new Date(project.prefinal_end) < new Date() ? '#C62828' : 'inherit',
                            fontWeight: new Date(project.prefinal_end) < new Date() ? 600 : 400,
                          }}>{String(project.prefinal_end).slice(0,10)}</span>
                        </div>
                      )}
                      {project.final_end && (
                        <div>
                          <strong>Final End:</strong>{' '}
                          <span style={{
                            color: new Date(project.final_end) < new Date() ? '#C62828' : 'inherit',
                            fontWeight: new Date(project.final_end) < new Date() ? 600 : 400,
                          }}>{String(project.final_end).slice(0,10)}</span>
                        </div>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ width: colWidths.pm, overflow: 'hidden' }}>
                  <Typography variant="body2" noWrap>{project.pm_name || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ width: colWidths.sales, overflow: 'hidden' }}>
                  <Typography variant="body2" noWrap color={(project.sales_name || project.sales_rep_name) ? 'text.primary' : 'text.secondary'}>
                    {/* Prefer the users-table join (sales_name) over the free-text
                        D-Tools string (sales_rep_name). The D-Tools column can be
                        stale (e.g. earliest proposal owner) and disagree with
                        sales_id, which caused reps to see other people's jobs
                        showing their own name (Marc Flamm, 2026-07-20). */}
                    {project.sales_name || project.sales_rep_name || '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: colWidths.bill, overflow: 'hidden' }}>
                  {project.current_phase >= 6 ? (
                    <Chip
                      label={(project.final_bill_status || 'not started').replace('_', ' ')}
                      size="small"
                      sx={{
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        bgcolor: project.final_bill_status === 'paid' ? '#e8f5e9'
                          : project.final_bill_status === 'released' ? '#e3f2fd'
                          : project.final_bill_status === 'started' ? '#fff3e0'
                          : '#ffebee',
                        color: project.final_bill_status === 'paid' ? '#2e7d32'
                          : project.final_bill_status === 'released' ? '#1565c0'
                          : project.final_bill_status === 'started' ? '#e65100'
                          : '#c62828',
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ width: colWidths.warranty, overflow: 'hidden' }}>
                  {project.warranty_start_date ? (() => {
                    const months = project.warranty_months || 12;
                    const expires = new Date(project.warranty_start_date);
                    expires.setMonth(expires.getMonth() + months);
                    const daysLeft = Math.ceil((expires - new Date()) / 86400000);
                    return (
                      <Chip
                        label={daysLeft < 0 ? 'Expired' : daysLeft < 30 ? `${daysLeft}d left` : `${Math.ceil(daysLeft/30)}mo left`}
                        size="small"
                        sx={{
                          fontWeight: 600, fontSize: '0.7rem',
                          bgcolor: daysLeft < 0 ? '#ffebee' : daysLeft < 30 ? '#fff3e0' : daysLeft < 90 ? '#e3f2fd' : '#e8f5e9',
                          color: daysLeft < 0 ? '#c62828' : daysLeft < 30 ? '#e65100' : daysLeft < 90 ? '#1565c0' : '#2e7d32',
                        }}
                      />
                    );
                  })() : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                {isManagement && (
                  <TableCell sx={{ width: colWidths.value, overflow: 'hidden' }}>
                    <Typography
                      variant="body2"
                      fontWeight={project.total_proposal_value > 0 ? 'bold' : 'normal'}
                      color={project.total_proposal_value > 0 ? 'success.main' : 'text.secondary'}
                    >
                      {fmtCurrency(project.total_proposal_value)}
                    </Typography>
                  </TableCell>
                )}
                {isManagement && (
                  <TableCell sx={{ width: colWidths.ar, overflow: 'hidden' }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {project.ar_number || '—'}
                    </Typography>
                  </TableCell>
                )}
                <TableCell sx={{ width: colWidths.health, overflow: 'hidden' }}>
                  <Chip
                    label={project.health_status}
                    size="small"
                    color={HEALTH_COLORS[project.health_status]}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>
              </TableRow>

              {/* ── Expanded detail row ── */}
              <TableRow>
                <TableCell colSpan={colCount} sx={{ p: 0, border: 0 }}>
                  <Collapse in={expandedRow === project.id} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'primary.light' }}>
                      <Grid container spacing={2}>

                        {/* Labor Hours */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Labor Hours
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                            <Typography variant="body2">
                              <strong>Sold:</strong> {project.labor_hours_sold ?? '—'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Used:</strong> {project.labor_hours_used ?? '—'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Remaining:</strong> {project.labor_hours_remaining ?? '—'}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Phase Progress */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Phase Progress
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            {project.current_phase}. {PHASE_NAMES[project.current_phase - 1]}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(project.current_phase / 7) * 100}
                            sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Phase {project.current_phase} of 7
                          </Typography>
                          {isManagement && project.margin_pct != null && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>Margin:</strong> {project.margin_pct}%
                            </Typography>
                          )}
                          {isManagement && project.dtools_cost != null && (
                            <Typography variant="body2">
                              <strong>Cost:</strong> {fmtCurrency(project.dtools_cost)}
                            </Typography>
                          )}
                          {project.progress_stage && (
                            <Typography variant="body2">
                              <strong>Stage:</strong> {project.progress_stage}
                            </Typography>
                          )}
                        </Grid>

                        {/* OptiPortal Reports */}
                        <Grid item xs={12} sm={6} md={3}>
                          {project.primary_job_number ? (
                            <>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                OptiPortal Reports
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {OPTI_REPORTS.map((r) => (
                                  <Chip
                                    key={r.key}
                                    label={r.label}
                                    size="small"
                                    component="a"
                                    href={`https://optiportal.audiocommand.com/Report-Viewer?key=${r.key}&_CRJob=${encodeURIComponent(project.primary_job_number)}&renderas=pdf`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    clickable
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      bgcolor: r.color,
                                      color: 'white',
                                      fontSize: '0.7rem',
                                      fontWeight: 'bold',
                                      '&:hover': { opacity: 0.85 },
                                    }}
                                  />
                                ))}
                              </Box>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No job number — OptiPortal links unavailable
                            </Typography>
                          )}
                        </Grid>

                        {/* Quick Actions */}
                        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 1 }}>
                          {canEditProject && project.current_phase < 7 && (
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<NavigateNextIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onPhaseChange(project.id, project.current_phase + 1, project.name);
                              }}
                            >
                              → {PHASE_NAMES[project.current_phase] || 'Next Phase'}
                            </Button>
                          )}
                          {canEditProject && project.current_phase > 1 && (
                            <Button
                              variant="outlined"
                              color="warning"
                              size="small"
                              startIcon={<NavigateBeforeIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onPhaseChange(project.id, project.current_phase - 1, project.name);
                              }}
                            >
                              ← {PHASE_NAMES[project.current_phase - 2]}
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }}
                          >
                            View Full Project →
                          </Button>
                        </Grid>

                      </Grid>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}

          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={colCount} sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No projects found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const Projects = () => {
  const { preferences, loaded: prefsLoaded } = usePreferences();
  const prefsInitialized = useRef(false);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'table'
  const [approvedOnly, setApprovedOnly] = useState(true); // true = Active Jobs Only, false = All Projects
  const [myProjectsOnly, setMyProjectsOnly] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isManagement = user?.role === 'Management' || user?.roles?.includes('Management');
  const canEditProject = isManagement || ['PM', 'Sales'].includes(user?.role) ||
    (Array.isArray(user?.roles) && (user.roles.includes('PM') || user.roles.includes('Sales')));

  const handlePhaseChange = async (projectId, newPhase, projectName, skipConfirm = false) => {
    const phaseName = PHASE_NAMES[newPhase - 1];
    if (!skipConfirm && !window.confirm(`Move "${projectName}" to ${phaseName} (Phase ${newPhase})?`)) return;
    try {
      await setProjectPhase(projectId, newPhase);
      // Update local state immediately
      setProjects(prev => prev.map(p =>
        p.id === projectId ? { ...p, current_phase: newPhase } : p
      ));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to change phase');
    }
  };

  // Initialize search + entity filters from URL params (Tom Clancy / Mike
  // Murray, 2026-04-24). The global top-bar search routes here with one of:
  //   ?search=<text>  — fuzzy text fallback (legacy)
  //   ?ar=<AR_CODE>   — navigate-and-filter to a single client's projects
  //   ?engagement_id=<id> — filter to all projects under an engagement
  // ar_label / engagement_label decorate the removable chip with a human name.
  const initialParams = new URLSearchParams(location.search);
  const initialSearch = initialParams.get('search') || '';
  const initialAr = (initialParams.get('ar') || '').toUpperCase();
  const initialArLabel = initialParams.get('ar_label') || '';
  const initialEngagementId = initialParams.get('engagement_id') || '';
  const initialEngagementLabel = initialParams.get('engagement_label') || '';
  // PMs default to viewing their own projects; everyone else sees all
  const isPM = user?.role === 'PM' || user?.roles?.includes('PM');
  // Shade users default to showing only shade projects
  const isShadeUser = ['Shade Design & Sales Manager', 'Shade Manager', 'Shade Tech', 'Shade Field Manager'].includes(user?.role);
  const isSales = user?.role === 'Sales' || user?.roles?.includes('Sales');
  const [filters, setFilters] = useState({
    health_status: '',
    pm_id: isPM ? String(user?.id || '') : '',
    sales_id: isSales ? String(user?.id || '') : '',
    search: initialSearch,
    // Discipline filter (Tom Clancy 2026-07-13). Shade users still
    // default to Shades so their view is unchanged.
    discipline: isShadeUser ? 'Shades' : '',
    ar_code: initialAr,
    ar_label: initialArLabel,
    engagement_id: initialEngagementId,
    engagement_label: initialEngagementLabel,
  });

  // Update filters when URL changes (e.g., from global search bar). Mirrors
  // every supported query param so navigating with new params re-filters
  // without a full reload.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const urlSearch = p.get('search') || '';
    const urlAr = (p.get('ar') || '').toUpperCase();
    const urlArLabel = p.get('ar_label') || '';
    const urlEng = p.get('engagement_id') || '';
    const urlEngLabel = p.get('engagement_label') || '';
    setFilters(f => {
      if (
        urlSearch === f.search &&
        urlAr === f.ar_code &&
        urlArLabel === f.ar_label &&
        urlEng === f.engagement_id &&
        urlEngLabel === f.engagement_label
      ) return f;
      return {
        ...f,
        search: urlSearch,
        ar_code: urlAr,
        ar_label: urlArLabel,
        engagement_id: urlEng,
        engagement_label: urlEngLabel,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const [pmList, setPmList] = useState([]);
  const [salesList, setSalesList] = useState([]);

  // Initialize viewMode from preferences once loaded (only on first load)
  useEffect(() => {
    if (prefsLoaded && !prefsInitialized.current) {
      prefsInitialized.current = true;
      const savedView = preferences?.dashboard?.defaultProjectView;
      if (savedView === 'kanban' || savedView === 'table') {
        setViewMode(savedView);
      }
    }
  }, [prefsLoaded, preferences]);

  // Load PM and Sales lists for filter dropdowns
  useEffect(() => {
    const loadFilterLists = async () => {
      try {
        const res = await getProjects({ approved: 'false' });
        const data = res.data || [];
        const pms = [...new Map(data.filter(p => p.pm_name).map(p => [p.pm_id, { id: p.pm_id, name: p.pm_name }])).values()];
        pms.sort((a, b) => a.name.localeCompare(b.name));
        setPmList(pms);
        // Build the Sales dropdown from the users-table join (sales_id +
        // sales_name) — not from the free-text D-Tools string. Deduping by
        // sales_id while labeling with sales_rep_name caused label/id
        // collisions where the visible name in the filter didn't match the
        // id it filtered by (e.g. "Marc Flamm" ended up mapped to Bill Hipp's
        // id because one 38 W 10th row had Marc as sales_rep_name while its
        // sales_id was Bill). Fixed 2026-07-20.
        const sales = [...new Map(
          data
            .filter(p => p.sales_id && p.sales_name)
            .map(p => [p.sales_id, { id: p.sales_id, name: p.sales_name }])
        ).values()];
        sales.sort((a, b) => a.name.localeCompare(b.name));
        setSalesList(sales);
      } catch {}
    };
    loadFilterLists();
  }, []);

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.health_status, filters.pm_id, filters.sales_id, filters.ar_code, filters.engagement_id, approvedOnly]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => loadProjects(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const params = { status: 'active' };
      if (filters.health_status) params.health_status = filters.health_status;
      if (filters.pm_id) params.pm_id = filters.pm_id;
      if (filters.sales_id) params.sales_id = filters.sales_id;
      if (filters.search) params.search = filters.search;
      // Entity filters from the global top-bar navigate-and-filter flow.
      if (filters.ar_code) params.ar_code = filters.ar_code;
      if (filters.engagement_id) params.engagement_id = filters.engagement_id;
      // Pass approved param — backend defaults to true but we make it explicit
      params.approved = approvedOnly ? 'true' : 'false';

      const response = await getProjects(params);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply client-side filters
  // Discipline filter (2026-07-13): derive discipline from job_type +
  // is_de_labor + name (see deriveDiscipline). Exact match against one
  // of AV, AV/IT, Shades, Lighting, Fixtures, D&E.
  let filteredProjects = filters.discipline
    ? projects.filter((p) => deriveDiscipline(p) === filters.discipline)
    : projects;

  // My Projects filter — show only projects where user is PM or Sales rep
  if (myProjectsOnly && user?.id) {
    filteredProjects = filteredProjects.filter(p => p.pm_id === user.id || p.sales_id === user.id);
  }

  // Total contract value of the filtered set. Tom Clancy 2026-04-30:
  // 'When I'm searching the WIP by Salesman and PM, I'd like to see the
  // total value of the projects they're working on.'
  //
  // Pat Kross 2026-07-08: the chip must match the WIP Excel export dollar-
  // for-dollar. That report sums sage_contract_amount (the Sage-side
  // contract number). Previously the chip summed total_proposal_value
  // (the D-Tools proposal number) — so the header on the screen and the
  // total in the exported spreadsheet disagreed by millions.
  //
  // Pat Kross / Chris Georghiou 2026-07-28: header chip and the "WIP by Job
  // Category" totals row (top strip) disagreed even with all filters set to
  // ALL. Root cause: the chip summed the whole approved list (Closed-Won in
  // D-Tools OR labor_hours_sold > 0), while the strip only counts
  // sage_status = 'In progress'. So the chip pulled in Sage-closed jobs
  // still tagged approved/contracted in CC (~57 rows, ~$3.29M) and dropped
  // 15 Sage in-progress jobs with no linked Closed-Won D-Tools deal (e.g.
  // 20-0562/0564/0566/0568/0570/0572 = 36 & 38 W 10th St equipment-only,
  // ACS East showroom Lutron SH, DDC Showroom AV, Scheiner Residence).
  // Same page, different denominators.
  // Fix (Option A, Tom 2026-07-28): chip now scopes to sage_status =
  // 'In progress' so it lines up with the strip and with how Pat reconciles
  // WIP off Sage. Filters still layer on top. Non-Sage-in-progress rows
  // remain visible in the table — they just don't count in the chip.
  const wipInProgressProjects = filteredProjects.filter(
    (p) => p.sage_status === 'In progress'
  );
  // Tom Clancy (2026-08-06): show a chip with the reconciled active-WIP
  // count — the same 189 (drifting) that /wip/active drives off. This is
  // the field team's actual active work, distinct from the broader Sage
  // in-progress list which includes legacy / stalled / not-yet-started.
  const reconciledActiveWipCount = wipInProgressProjects.filter(
    (p) => p.is_active_wip === true
  ).length;
  const filteredTotalValue = isManagement
    ? wipInProgressProjects.reduce(
        (sum, p) => sum + (Number(p.sage_contract_amount) || 0),
        0
      )
    : null;

  // Data quality stats
  const noTimelineCount = filteredProjects.filter(p => !p.projected_completion_date && !p.sage_est_comp && p.current_phase >= 2).length;
  const staleCount = filteredProjects.filter(p => p.started_at && ((Date.now() - new Date(p.started_at).getTime()) / 86400000) > 90 && p.current_phase < 6).length;
  const noPmCount = filteredProjects.filter(p => !p.pm_id).length;
  const hasDataIssues = noTimelineCount > 0 || staleCount > 0 || noPmCount > 0;

  // Pagination
  const [tablePage, setTablePage] = useState(1);
  const rowsPerPage = 50;
  const totalPages = Math.ceil(filteredProjects.length / rowsPerPage);
  const paginatedProjects = filteredProjects.slice((tablePage - 1) * rowsPerPage, tablePage * rowsPerPage);

  const projectsByPhase = {};
  for (let i = 1; i <= 7; i++) {
    projectsByPhase[i] = filteredProjects.filter((p) => p.current_phase === i);
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>
            WIP (Work in Progress)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: '#8898A4' }}>
              {filteredProjects.length} {approvedOnly ? 'active / contracted' : ''} projects
            </Typography>
            {reconciledActiveWipCount > 0 && (
              <Tooltip title={
                <Box sx={{ fontSize: '0.75rem' }}>
                  <div><strong>Reconciled Active WIP</strong> — the {reconciledActiveWipCount} Sage-in-progress job{reconciledActiveWipCount === 1 ? '' : 's'} that the field team has confirmed as actively worked (is_active_wip=TRUE).</div>
                  <div style={{ marginTop: 4, opacity: 0.85 }}>
                    Matches the /wip/active page (Active Projects) and the 'Active Projects' KPI on the dashboard.
                  </div>
                </Box>
              }>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`Reconciled active: ${reconciledActiveWipCount}`}
                  onClick={() => navigate('/wip/active')}
                  sx={{ fontWeight: 700, cursor: 'pointer' }}
                />
              </Tooltip>
            )}
            {isManagement && filteredTotalValue !== null && wipInProgressProjects.length > 0 && !filters.discipline && (
              <Tooltip title={
                <Box sx={{ fontSize: '0.75rem' }}>
                  <div><strong>Sage WIP Total</strong> &mdash; sum of sage_contract_amount across the {wipInProgressProjects.length} Sage in-progress job{wipInProgressProjects.length === 1 ? '' : 's'} matching the current filters.</div>
                  <div style={{ marginTop: 4, opacity: 0.85 }}>
                    Ties out dollar-for-dollar to the &ldquo;WIP by Job Category&rdquo; totals row above. Non-Sage-in-progress rows (proposal-stage or Sage-closed) still show in the table below, but aren&rsquo;t counted here.
                  </div>
                  {(filters.pm_id || filters.sales_id) && (
                    <div style={{ marginTop: 4, opacity: 0.85 }}>
                      Filtered by{filters.pm_id ? ` PM “${pmList.find(p => String(p.id) === String(filters.pm_id))?.name || filters.pm_id}”` : ''}{(filters.pm_id && filters.sales_id) ? ' and ' : ''}{filters.sales_id ? ` Sales “${salesList.find(s => String(s.id) === String(filters.sales_id))?.name || filters.sales_id}”` : ''}.
                    </div>
                  )}
                </Box>
              }>
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`Sage WIP Total: ${fmtCurrency(filteredTotalValue)}`}
                  sx={{ fontWeight: 700, cursor: 'help' }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search by name or job number */}
          <PageSearchBar
            placeholder="Search name, client, or job #…"
            value={filters.search}
            onChange={(v) => setFilters({ ...filters, search: v })}
            onClear={() => setFilters({ ...filters, search: '' })}
          />

          {/* Active entity filters — set by the global top-bar search when the
              user picks a Client or an Engagement (Tom Clancy / Mike Murray,
              2026-04-24). Each chip is removable; clearing it drops the URL
              param so the filter stops being active. */}
          {filters.ar_code && (
            <Chip
              size="small"
              color="primary"
              variant="filled"
              label={`Client: ${filters.ar_label || filters.ar_code}`}
              onDelete={() => {
                const p = new URLSearchParams(location.search);
                p.delete('ar');
                p.delete('ar_label');
                navigate(`${location.pathname}${p.toString() ? '?' + p.toString() : ''}`);
              }}
              sx={{ fontWeight: 600 }}
            />
          )}
          {filters.engagement_id && (
            <Chip
              size="small"
              color="info"
              variant="filled"
              label={`Engagement: ${filters.engagement_label || `#${filters.engagement_id}`}`}
              onDelete={() => {
                const p = new URLSearchParams(location.search);
                p.delete('engagement_id');
                p.delete('engagement_label');
                navigate(`${location.pathname}${p.toString() ? '?' + p.toString() : ''}`);
              }}
              sx={{ fontWeight: 600 }}
            />
          )}

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Health Status</InputLabel>
            <Select
              value={filters.health_status}
              label="Health Status"
              onChange={(e) => setFilters({ ...filters, health_status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="green">Green</MenuItem>
              <MenuItem value="yellow">Yellow</MenuItem>
              <MenuItem value="red">Red</MenuItem>
            </Select>
          </FormControl>

          {/* PM Filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Project Manager</InputLabel>
            <Select
              value={filters.pm_id}
              label="Project Manager"
              onChange={(e) => setFilters({ ...filters, pm_id: e.target.value })}
            >
              <MenuItem value="">All PMs</MenuItem>
              {pmList.map((pm) => (
                <MenuItem key={pm.id} value={pm.id}>{pm.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sales Person Filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sales Person</InputLabel>
            <Select
              value={filters.sales_id}
              label="Sales Person"
              onChange={(e) => setFilters({ ...filters, sales_id: e.target.value })}
            >
              <MenuItem value="">All Sales</MenuItem>
              {salesList.map((sp) => (
                <MenuItem key={sp.id} value={sp.id}>{sp.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Discipline Filter (Tom Clancy 2026-07-13):
              AV, AV/IT, Shades, Lighting, Fixtures, D&E — derived from
              job_type + is_de_labor + project name. */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Discipline</InputLabel>
            <Select
              value={filters.discipline}
              label="Discipline"
              onChange={(e) => setFilters({ ...filters, discipline: e.target.value })}
            >
              <MenuItem value="">All Disciplines</MenuItem>
              {DISCIPLINES.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Active Jobs Only / Show All toggle */}
          <Tooltip title={approvedOnly ? 'Showing Closed Won / contracted jobs only — click to show all' : 'Showing all projects including proposals — click to filter to active jobs'}>
            <ToggleButtonGroup
              value={approvedOnly ? 'active' : 'all'}
              exclusive
              onChange={(e, val) => { if (val !== null) setApprovedOnly(val === 'active'); }}
              size="small"
            >
              <ToggleButton value="active" color="success">
                <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} />
                Active Jobs
              </ToggleButton>
              <ToggleButton value="all">
                All Projects
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>

          {/* My Projects toggle */}
          <Tooltip title={myProjectsOnly ? 'Showing only projects assigned to you — click to show all' : 'Click to filter to your projects only'}>
            <ToggleButton
              value="mine"
              selected={myProjectsOnly}
              onChange={() => setMyProjectsOnly(!myProjectsOnly)}
              size="small"
              color="primary"
              sx={{ textTransform: 'none', px: 1.5 }}
            >
              My Projects
            </ToggleButton>
          </Tooltip>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            size="small"
          >
            <ToggleButton value="kanban">
              <Tooltip title="Kanban">
                <ViewKanbanIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table">
              <Tooltip title="Table">
                <TableRowsIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const resp = await fetch('/api/projects/export/wip', {
                  headers: { Authorization: `Bearer ${token}` }
                });
                const blob = await resp.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `ACS_WIP_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (err) {
                console.error('Export failed:', err);
              }
            }}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Export to Excel
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/projects/new')}
            disabled
            title="Projects are created in D-Tools/Zoho"
          >
            New Project
          </Button>
        </Box>
      </Box>

      {/* WIP by Discipline (live rollup) — Jonathan Flamm's July 2026 ask:
          break the single WIP number into AV / LTG / SHD / IT / HT, with
          WIP-to-Complete alongside contract and % complete. Data ties out to
          Pat Kross's WIP_to_Complete_for_CC.xlsx (2026-07-15) dollar-for-
          dollar on Contract, Estimate, Billed. Management-only because it
          exposes contract totals. */}
      {isManagement && <WipByDisciplineStrip filters={filters} />}

      {/* Data Quality Alert */}
      {!loading && hasDataIssues && canEditProject && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Data Quality:</strong>
          {noTimelineCount > 0 && ` ${noTimelineCount} projects have no completion date ·`}
          {staleCount > 0 && ` ${staleCount} projects stale 90+ days ·`}
          {noPmCount > 0 && ` ${noPmCount} projects have no PM assigned`}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'kanban' ? (
        <Box sx={{ overflowX: 'auto', pb: 2 }}>
          <Grid container spacing={2} sx={{ minWidth: 1200 }}>
            {[1, 2, 3, 4, 5, 6, 7].map((phase) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={phase}>
                <PhaseColumn
                  phase={phase}
                  projects={projectsByPhase[phase]}
                  onProjectClick={(id) => navigate(`/projects/${id}`)}
                  isManagement={isManagement}
                  canEditProject={canEditProject}
                  onPhaseChange={handlePhaseChange}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <>
          <ProjectTable
            projects={paginatedProjects}
            onProjectClick={(id) => navigate(`/projects/${id}`)}
            isManagement={isManagement}
            canEditProject={canEditProject}
            onPhaseChange={handlePhaseChange}
          />
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Showing {((tablePage - 1) * rowsPerPage) + 1}–{Math.min(tablePage * rowsPerPage, filteredProjects.length)} of {filteredProjects.length} projects
              </Typography>
              <Pagination count={totalPages} page={tablePage} onChange={(e, p) => setTablePage(p)} color="primary" size="small" />
            </Box>
          )}
        </>)
      }
    </Box>
  );
};

export default Projects;
